import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Container, Stack, Typography } from '@mui/material';
import Table from '../../../../atoms/Table/Table';
import Searcher from '../../../../molecules/Searcher';
import EmptyBlock from '../../../../molecules/EmptyBlock';
import {
  OwnerEntityAddressColumns,
  MAX_AMOUNT_OF_ITEMS_ON_PAGE,
} from './OwnerEntityAddress.constants';
import { getOwnerEntityAddresses } from '../../../../../store/slices/editOwnerEntitySlice';
import StateLink from '../../../../atoms/StateLink/StateLink';

const OwnerEntityAddress = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState('');
  const [isLoader, setIsLoader] = useState(true);
  const [isLoaderTable, setIsLoaderTable] = useState(false);
  const {
    state: { id },
  } = useLocation();
  const addresses = useSelector((state) => state.editOwnerEntity.addresses);

  useEffect(() => {
    dispatch(getOwnerEntityAddresses({ id, pageSize: MAX_AMOUNT_OF_ITEMS_ON_PAGE })).then(() =>
      setIsLoader(false),
    );
  }, [id]);

  const handleChangeEmpty = useCallback((searchElement) => {
    if (!searchElement) {
      setIsLoaderTable(true);
      dispatch(getOwnerEntityAddresses({ id, pageSize: MAX_AMOUNT_OF_ITEMS_ON_PAGE })).then(() => {
        setIsLoaderTable(false);
        setSearchValue('');
      });
    }
  }, []);

  const handleSearch = useCallback((searchElement) => {
    setSearchValue(searchElement);
    setIsLoaderTable(true);
    dispatch(
      getOwnerEntityAddresses({
        id,
        pageSize: MAX_AMOUNT_OF_ITEMS_ON_PAGE,
        address: searchElement,
      }),
    ).then(() => {
      setIsLoaderTable(false);
    });
  }, []);

  const handleFetchDataPage = useCallback(
    (currentPage) => {
      setIsLoaderTable(true);
      dispatch(
        getOwnerEntityAddresses({
          id,
          currentPage,
          pageSize: MAX_AMOUNT_OF_ITEMS_ON_PAGE,
          address: searchValue,
        }),
      ).then(() => {
        setIsLoaderTable(false);
      });
    },
    [searchValue],
  );

  if (isLoader)
    return (
      <Box position="absolute" top="50vh" left="50%" zIndex={10} transform="translate(-50%, -50%)">
        <CircularProgress />
      </Box>
    );

  if (!isLoader && !addresses?.items?.length && !searchValue) {
    return (
      <Stack height="calc(100vh - 80px - 72px)" direction="column">
        <Box margin="auto">
          <EmptyBlock title="You don’t have any owner entity addresses yet" />
        </Box>
      </Stack>
    );
  }

  const rowsAdapter = addresses?.items?.map((item) => ({
    ...item,
    address: (
      <StateLink
        to="/db/datahandling/editaddress/address"
        state={{
          navPage: 'Data Handling',
          subNav: 'Addresses',
          id: item.addressId,
          name: item.address,
          state: { value: item.stateCode },
        }}
        alwaysOpenInNewTab
      >
        <Typography
          variant="link"
          color="text.primary"
          sx={{
            cursor: 'pointer',
            textDecorationLine: 'underline',
            textTransform: 'none',
          }}
        >
          {item.address}
        </Typography>
      </StateLink>
    ),
    control: {
      hideDelete: true,
      autoRefresh: true,
    },
  }));

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        mx: 'auto',
        mt: 4,
        px: 4,
        maxWidth: 1066,
        width: 1,
        pb: 18,
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h5">Owner Entity Addresses</Typography>
        <Searcher
          inputPlaceholder="Search by Address"
          onSearch={handleSearch}
          onChange={handleChangeEmpty}
        />
        {!!addresses?.items?.length && !isLoader && (
          <Table
            columns={OwnerEntityAddressColumns}
            rows={rowsAdapter}
            total={addresses?.totalRowsCount}
            isPagination={addresses?.totalPages > 1}
            onNextPage={handleFetchDataPage}
            onPreviousPage={handleFetchDataPage}
            onGotoPage={handleFetchDataPage}
            pageSize={MAX_AMOUNT_OF_ITEMS_ON_PAGE}
            loading={isLoaderTable}
          />
        )}
        {!addresses?.items?.length && !isLoader && searchValue && (
          <Stack direction="column" paddingTop="50px">
            <Box margin="auto">
              <EmptyBlock
                title="Owner entity addresses not found"
                desc="Сhange the search value"
                margin="auto"
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default OwnerEntityAddress;
